import React, { useEffect, useState, useCallback } from 'react';
import {
  Row, Col, Card, Button, Modal, Form, Input, InputNumber,
  Select, Space, Tag, Typography, message, Popconfirm, Tabs, Upload, Empty, Segmented, Alert,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, UploadOutlined, FileTextOutlined,
  QuestionCircleOutlined, CheckCircleOutlined, WarningOutlined, BookOutlined,
  EyeOutlined, EditOutlined, SendOutlined, UndoOutlined,
} from '@ant-design/icons';
import { govtApi } from '../services/api';
import NotesTabPanel from '../components/notes/NotesTabPanel';
import type { NcertChapterNote } from '../components/notes/types';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface NcertClass { id: string; name: string; display_name: string }
interface NcertSubject { id: string; class_id: string; name: string }
interface NcertChapter {
  id: string; subject_id: string; name: string; chapter_number: number;
  source_pdf_key?: string; pdf_page_count?: number; pdf_char_count?: number;
  question_count: number;
}
interface NcertQuestion {
  id: string; question: string; option_a: string; option_b: string;
  option_c: string; option_d: string; answer: string; explanation?: string;
}

const NcertPrepPage: React.FC = () => {
  const [classes, setClasses]   = useState<NcertClass[]>([]);
  const [subjects, setSubjects] = useState<NcertSubject[]>([]);
  const [chapters, setChapters] = useState<NcertChapter[]>([]);
  const [questions, setQuestions] = useState<NcertQuestion[]>([]);
  const [chapterNotes, setChapterNotes] = useState<NcertChapterNote[]>([]);

  const [selectedClassId, setSelectedClassId]     = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [activeTab, setActiveTab] = useState('subjects');
  const [notesSubTab, setNotesSubTab] = useState<'important' | 'pocket' | 'timeline'>('important');
  const [notesMode, setNotesMode] = useState<'edit' | 'preview'>('edit');
  const [savingNotes, setSavingNotes] = useState(false);

  const [subjectModal, setSubjectModal]   = useState(false);
  const [chapterModal, setChapterModal]   = useState(false);
  const [questionModal, setQuestionModal] = useState(false);
  const [editingChapter, setEditingChapter] = useState<NcertChapter | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const [subjectForm]  = Form.useForm();
  const [chapterForm]  = Form.useForm();
  const [questionForm] = Form.useForm();

  const loadClasses = useCallback(async () => {
    try {
      const res = await govtApi.get('/govt/ncert/classes');
      setClasses(res.data);
      if (res.data.length && !selectedClassId) setSelectedClassId(res.data[0].id);
    } catch { message.error('Failed to load classes'); }
  }, [selectedClassId]);

  const loadSubjects = useCallback(async (classId: string) => {
    if (!classId) return;
    try {
      const res = await govtApi.get('/govt/admin/ncert/subjects', { params: { classId } });
      setSubjects(res.data);
    } catch { message.error('Failed to load subjects'); }
  }, []);

  const loadChapters = useCallback(async (subjectId: string) => {
    if (!subjectId) { setChapters([]); return; }
    try {
      const res = await govtApi.get('/govt/admin/ncert/chapters', { params: { subjectId } });
      setChapters(res.data);
    } catch { message.error('Failed to load chapters'); }
  }, []);

  const loadQuestions = useCallback(async (chapterId: string) => {
    if (!chapterId) { setQuestions([]); return; }
    try {
      const res = await govtApi.get(`/govt/admin/ncert/chapters/${chapterId}/questions`);
      setQuestions(res.data);
    } catch { message.error('Failed to load questions'); }
  }, []);

  const loadNotes = useCallback(async (chapterId: string) => {
    if (!chapterId) { setChapterNotes([]); return; }
    try {
      const res = await govtApi.get(`/govt/admin/ncert/chapters/${chapterId}/notes`);
      setChapterNotes(res.data);
    } catch { message.error('Failed to load notes'); }
  }, []);

  useEffect(() => { loadClasses(); }, [loadClasses]);
  useEffect(() => {
    setSelectedSubjectId(''); setSelectedChapterId('');
    if (selectedClassId) loadSubjects(selectedClassId);
  }, [selectedClassId, loadSubjects]);
  useEffect(() => {
    setSelectedChapterId('');
    loadChapters(selectedSubjectId);
  }, [selectedSubjectId, loadChapters]);
  useEffect(() => { loadQuestions(selectedChapterId); }, [selectedChapterId, loadQuestions]);
  useEffect(() => { loadNotes(selectedChapterId); }, [selectedChapterId, loadNotes]);

  // ── Subjects ──────────────────────────────────────────────────────────────
  const saveSubject = async () => {
    const v = await subjectForm.validateFields();
    try {
      await govtApi.post('/govt/admin/ncert/subjects', { class_id: selectedClassId, name: v.name });
      message.success('Subject added');
      setSubjectModal(false);
      loadSubjects(selectedClassId);
    } catch { message.error('Failed to add subject'); }
  };

  const deleteSubject = async (id: string) => {
    try {
      await govtApi.delete(`/govt/admin/ncert/subjects/${id}`);
      message.success('Subject deleted');
      loadSubjects(selectedClassId);
    } catch { message.error('Failed to delete subject'); }
  };

  // ── Chapters ──────────────────────────────────────────────────────────────
  const openChapterModal = (chapter?: NcertChapter) => {
    setEditingChapter(chapter || null);
    chapterForm.setFieldsValue(chapter ? { name: chapter.name, chapter_number: chapter.chapter_number } : { name: '', chapter_number: chapters.length + 1 });
    setChapterModal(true);
  };

  const saveChapter = async () => {
    const v = await chapterForm.validateFields();
    try {
      if (editingChapter) {
        await govtApi.put(`/govt/admin/ncert/chapters/${editingChapter.id}`, v);
        message.success('Chapter updated');
      } else {
        await govtApi.post('/govt/admin/ncert/chapters', { ...v, subject_id: selectedSubjectId });
        message.success('Chapter created');
      }
      setChapterModal(false);
      loadChapters(selectedSubjectId);
    } catch { message.error('Failed to save chapter'); }
  };

  const deleteChapter = async (id: string) => {
    try {
      await govtApi.delete(`/govt/admin/ncert/chapters/${id}`);
      message.success('Chapter deleted');
      loadChapters(selectedSubjectId);
    } catch { message.error('Failed to delete chapter'); }
  };

  const uploadChapterPdf = async (chapterId: string, file: File) => {
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await govtApi.post(`/govt/admin/ncert/chapters/${chapterId}/pdf`, formData, {
        headers: { 'Content-Type': undefined },
      });
      if (res.data.looks_scanned) {
        message.warning('PDF uploaded, but very little text could be extracted — this may be a scanned/image-only PDF.');
      } else {
        message.success(`PDF uploaded (${res.data.pdf_page_count} pages, ${res.data.pdf_char_count} characters extracted)`);
      }
      loadChapters(selectedSubjectId);
    } catch {
      message.error('PDF upload failed');
    } finally {
      setUploadingPdf(false);
    }
  };

  // ── Questions ─────────────────────────────────────────────────────────────
  const saveQuestion = async () => {
    const v = await questionForm.validateFields();
    try {
      await govtApi.post(`/govt/admin/ncert/chapters/${selectedChapterId}/questions`, v);
      message.success('Question added');
      setQuestionModal(false);
      questionForm.resetFields();
      loadChapters(selectedSubjectId); // refresh question_count on chapter cards
      loadQuestions(selectedChapterId);
    } catch { message.error('Failed to add question'); }
  };

  const deleteQuestion = async (id: string) => {
    try {
      await govtApi.delete(`/govt/admin/ncert/questions/${id}`);
      message.success('Question deleted');
      loadChapters(selectedSubjectId);
      loadQuestions(selectedChapterId);
    } catch { message.error('Failed to delete question'); }
  };

  // ── Notes ─────────────────────────────────────────────────────────────────
  const currentNote = chapterNotes.find((n) => n.note_type === notesSubTab) || null;

  const saveNoteContent = async (content: object) => {
    setSavingNotes(true);
    try {
      if (currentNote) {
        await govtApi.put(`/govt/admin/ncert/notes/${currentNote.id}`, { content });
      } else {
        await govtApi.post(`/govt/admin/ncert/chapters/${selectedChapterId}/notes`, { note_type: notesSubTab, content });
      }
      message.success('Saved');
      loadNotes(selectedChapterId);
    } catch { message.error('Failed to save'); }
    finally { setSavingNotes(false); }
  };

  const setNoteStatus = async (id: string, status: 'pending_review' | 'published') => {
    try {
      await govtApi.put(`/govt/admin/ncert/notes/${id}/status`, { status });
      message.success(status === 'published' ? 'Published — now visible to students' : 'Unpublished');
      loadNotes(selectedChapterId);
    } catch { message.error('Failed to update status'); }
  };

  const deleteNote = async (id: string) => {
    try {
      await govtApi.delete(`/govt/admin/ncert/notes/${id}`);
      message.success('Deleted');
      loadNotes(selectedChapterId);
    } catch { message.error('Failed to delete'); }
  };

  const selectedChapter = chapters.find((c) => c.id === selectedChapterId);

  return (
    <div>
      <Space align="center" style={{ marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0 }}>NCERT Prep</Title>
        <Text type="secondary">Class-wise practice content for govt exam GK/GS sections</Text>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Text strong>Class:</Text>
          <Select
            style={{ width: 160 }}
            value={selectedClassId || undefined}
            placeholder="Select class"
            onChange={setSelectedClassId}
            options={classes.map((c) => ({ value: c.id, label: c.display_name }))}
          />
        </Space>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'subjects',
            label: 'Subjects',
            children: (
              <Card
                title={`Subjects`}
                extra={<Button icon={<PlusOutlined />} onClick={() => { subjectForm.resetFields(); setSubjectModal(true); }}>Add Subject</Button>}>
                <Row gutter={[12, 12]}>
                  {subjects.map((s) => (
                    <Col key={s.id} xs={24} sm={12} md={8} lg={6}>
                      <Card
                        hoverable
                        onClick={() => { setSelectedSubjectId(s.id); setActiveTab('chapters'); }}
                        style={{ borderColor: selectedSubjectId === s.id ? '#1D4ED8' : undefined }}
                        actions={[
                          <Popconfirm title="Delete this subject?" onConfirm={(e) => { e?.stopPropagation(); deleteSubject(s.id); }}>
                            <DeleteOutlined onClick={(e) => e.stopPropagation()} />
                          </Popconfirm>,
                        ]}>
                        <Text strong>{s.name}</Text>
                      </Card>
                    </Col>
                  ))}
                  {subjects.length === 0 && <Col span={24}><Empty description="No subjects for this class yet" /></Col>}
                </Row>
              </Card>
            ),
          },
          {
            key: 'chapters',
            label: 'Chapters',
            disabled: !selectedSubjectId,
            children: (
              <Card
                title={`Chapters — ${subjects.find((s) => s.id === selectedSubjectId)?.name ?? ''}`}
                extra={<Button icon={<PlusOutlined />} onClick={() => openChapterModal()} disabled={!selectedSubjectId}>Add Chapter</Button>}>
                <Row gutter={[12, 12]}>
                  {chapters.map((c) => (
                    <Col key={c.id} xs={24} sm={12} md={8}>
                      <Card
                        hoverable
                        onClick={() => { setSelectedChapterId(c.id); setActiveTab('questions'); }}
                        style={{ borderColor: selectedChapterId === c.id ? '#1D4ED8' : undefined }}
                        actions={[
                          <Upload
                            showUploadList={false}
                            accept="application/pdf"
                            customRequest={({ file }) => uploadChapterPdf(c.id, file as File)}
                            disabled={uploadingPdf}>
                            <UploadOutlined onClick={(e) => e.stopPropagation()} title="Upload chapter PDF" />
                          </Upload>,
                          <FileTextOutlined onClick={(e) => { e.stopPropagation(); openChapterModal(c); }} title="Edit" />,
                          <Popconfirm title="Delete this chapter?" onConfirm={(e) => { e?.stopPropagation(); deleteChapter(c.id); }}>
                            <DeleteOutlined onClick={(e) => e.stopPropagation()} />
                          </Popconfirm>,
                        ]}>
                        <Text strong>Ch. {c.chapter_number}: {c.name}</Text>
                        <div style={{ marginTop: 8 }}>
                          <Tag icon={<QuestionCircleOutlined />}>{c.question_count} question{c.question_count === 1 ? '' : 's'}</Tag>
                          {c.source_pdf_key ? (
                            (c.pdf_char_count ?? 0) / Math.max(c.pdf_page_count ?? 1, 1) < 50 ? (
                              <Tag color="warning" icon={<WarningOutlined />}>PDF: low text</Tag>
                            ) : (
                              <Tag color="success" icon={<CheckCircleOutlined />}>PDF uploaded</Tag>
                            )
                          ) : (
                            <Tag>No PDF yet</Tag>
                          )}
                        </div>
                      </Card>
                    </Col>
                  ))}
                  {chapters.length === 0 && <Col span={24}><Empty description="No chapters yet" /></Col>}
                </Row>
              </Card>
            ),
          },
          {
            key: 'questions',
            label: 'Questions',
            disabled: !selectedChapterId,
            children: (
              <Card
                title={`Questions — ${selectedChapter ? `Ch. ${selectedChapter.chapter_number}: ${selectedChapter.name}` : ''}`}
                extra={<Button icon={<PlusOutlined />} onClick={() => { questionForm.resetFields(); setQuestionModal(true); }} disabled={!selectedChapterId}>Add Question</Button>}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {questions.map((q, i) => (
                    <Card key={q.id} size="small">
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Text strong>{i + 1}. {q.question}</Text>
                          <Popconfirm title="Delete this question?" onConfirm={() => deleteQuestion(q.id)}>
                            <Button size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                        <Text type="secondary">A) {q.option_a}  B) {q.option_b}  C) {q.option_c}  D) {q.option_d} — Answer: {q.answer}</Text>
                      </Space>
                    </Card>
                  ))}
                  {questions.length === 0 && <Empty description="No questions linked to this chapter yet" />}
                </Space>
              </Card>
            ),
          },
          {
            key: 'notes',
            label: 'Notes',
            disabled: !selectedChapterId,
            children: (
              <NotesTabPanel
                chapterTitle={selectedChapter ? `Ch. ${selectedChapter.chapter_number}: ${selectedChapter.name}` : ''}
                notes={chapterNotes}
                subTab={notesSubTab}
                onSubTabChange={setNotesSubTab}
                mode={notesMode}
                onModeChange={setNotesMode}
                saving={savingNotes}
                onSaveContent={saveNoteContent}
                onSetStatus={setNoteStatus}
                onDelete={deleteNote}
              />
            ),
          },
        ]}
      />

      {/* Add Subject */}
      <Modal title="Add Subject" open={subjectModal} onOk={saveSubject} onCancel={() => setSubjectModal(false)}>
        <Form form={subjectForm} layout="vertical">
          <Form.Item name="name" label="Subject Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. History" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add/Edit Chapter */}
      <Modal title={editingChapter ? 'Edit Chapter' : 'Add Chapter'} open={chapterModal} onOk={saveChapter} onCancel={() => setChapterModal(false)}>
        <Form form={chapterForm} layout="vertical">
          <Form.Item name="chapter_number" label="Chapter Number" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
          <Form.Item name="name" label="Chapter Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. The French Revolution" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Question */}
      <Modal title="Add Question" open={questionModal} onOk={saveQuestion} onCancel={() => setQuestionModal(false)} width={700}>
        <Form form={questionForm} layout="vertical">
          <Form.Item name="question" label="Question" rules={[{ required: true }]}>
            <TextArea rows={2} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="option_a" label="Option A" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="option_b" label="Option B" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="option_c" label="Option C" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="option_d" label="Option D" rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="answer" label="Correct Answer" rules={[{ required: true }]}>
                <Select options={['A', 'B', 'C', 'D'].map((v) => ({ value: v, label: `Option ${v}` }))} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="explanation" label="Explanation (optional)">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default NcertPrepPage;
